import { useState, type FormEvent } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError, type AppSettings } from "@/lib/api";

type NumKey = Exclude<keyof AppSettings, "mentor_system_prompt" | "admin_alert_email" | "updated_at" | "terms_version" | "privacy_version">;

const LEGAL_FIELDS = [
  { key: "terms_version", label: "Versión de Términos y Condiciones" },
  { key: "privacy_version", label: "Versión del Aviso de Privacidad" },
] as const;

const GROUPS: { title: string; fields: { key: NumKey; label: string; hint: string; step?: string }[] }[] = [
  {
    title: "Suscripción",
    fields: [
      { key: "grace_period_days_after_failed_payment", label: "Días de gracia tras cobro fallido", hint: "grace_period_days_after_failed_payment" },
    ],
  },
  {
    title: "Límites del mentor",
    fields: [
      { key: "mentor_monthly_limit_default", label: "Mensajes por mes", hint: "mentor_monthly_limit_default" },
      { key: "mentor_hourly_limit_default", label: "Mensajes por hora", hint: "mentor_hourly_limit_default" },
      { key: "mentor_question_max_chars", label: "Máx. caracteres por pregunta", hint: "mentor_question_max_chars" },
      { key: "mentor_response_max_tokens", label: "Máx. tokens de respuesta", hint: "mentor_response_max_tokens" },
      { key: "daily_ai_budget_usd", label: "Presupuesto diario IA (USD)", hint: "daily_ai_budget_usd", step: "0.01" },
    ],
  },
];

export function AdminSettingsForm({ initial }: { initial: AppSettings }) {
  const [values, setValues] = useState<AppSettings>(initial);
  const [saving, setSaving] = useState(false);
  const setField = (key: keyof AppSettings, v: string | number) => setValues((s) => ({ ...s, [key]: v }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put<AppSettings>("/admin/settings", values);
      setValues(data);
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6" data-testid="admin-settings-form">
      {GROUPS.map((g) => (
        <fieldset key={g.title} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
          <legend className="sr-only">{g.title}</legend>
          <h3 className="font-display text-base font-bold text-[#1B2A4A]">{g.title}</h3>
          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {g.fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label htmlFor={f.key} className="text-sm text-slate-700">{f.label}</Label>
                <Input id={f.key} type="number" min={0} step={f.step || "1"} required value={values[f.key]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                  className="h-10 font-mono" data-testid={`setting-${f.key}-input`} />
                <p className="font-mono text-[11px] text-slate-400">{f.hint}</p>
              </div>
            ))}
          </div>
        </fieldset>
      ))}
      <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7" data-testid="admin-legal-versions">
        <h3 className="font-display text-base font-bold text-[#1B2A4A]">Textos legales</h3>
        <p className="mt-1 text-sm text-slate-500">Si cambias una fecha, cada usuario tendrá que aceptar la nueva versión la próxima vez que entre.</p>
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          {LEGAL_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label htmlFor={f.key} className="text-sm text-slate-700">{f.label}</Label>
              <Input id={f.key} type="date" required value={values[f.key]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField(f.key, e.target.value)}
                className="h-10 font-mono" data-testid={`setting-${f.key}-input`} />
            </div>
          ))}
        </div>
      </fieldset>
      <fieldset className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
        <h3 className="font-display text-base font-bold text-[#1B2A4A]">Mentor y alertas</h3>
        <div className="mt-5 space-y-5">
          <div className="max-w-md space-y-1.5">
            <Label htmlFor="admin_alert_email" className="text-sm text-slate-700">Email de alertas admin</Label>
            <Input id="admin_alert_email" type="email" required value={values.admin_alert_email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField("admin_alert_email", e.target.value)}
              className="h-10" data-testid="setting-admin_alert_email-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="mentor_system_prompt" className="text-sm text-slate-700">System prompt del mentor</Label>
            <Textarea id="mentor_system_prompt" required rows={8} value={values.mentor_system_prompt}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setField("mentor_system_prompt", e.target.value)}
              className="min-h-[180px] text-sm leading-relaxed" data-testid="setting-mentor_system_prompt-input" />
            <p className="text-right font-mono text-[11px] text-slate-400" data-testid="prompt-char-count">{values.mentor_system_prompt.length} caracteres</p>
          </div>
        </div>
      </fieldset>
      <div className="sticky bottom-4 flex justify-end">
        <Button type="submit" disabled={saving} className="h-11 rounded-full bg-amber-500 px-6 font-semibold text-[#1B2A4A] shadow-md hover:bg-amber-400" data-testid="admin-settings-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Guardar cambios</>}
        </Button>
      </div>
    </form>
  );
}
